import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import type {GeometryAttributeSetData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeSetNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import {Operators} from "#template/geometry-processing/geometry-graph"
import {
    applyGeometryOperator,
    geometryAttributeSetDataToGeometryAttributeRefDataSet,
    geometryAttributeRefDataSetToGeometryAttributeSetData,
} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"
import type {AttributeRefSet} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export type MergeOperatorParameters<TAttributeSet> = {
    name: string
    attributeSets: TAttributeSet[]
}

export const mergeOperatorParametersSchema = <TAttributeSet>(attributeSetSchema: z.ZodMiniType<TAttributeSet, TAttributeSet>) =>
    z.object({
        name: z.string(),
        attributeSets: z.array(attributeSetSchema),
    })

function applyMergeOperator<T extends IndexingDim>(attributeSets: GeometryAttributeSetData<T>[]): GeometryAttributeSetData<T> {
    const attrRefDataSets = attributeSets.map((attributeSet) => geometryAttributeSetDataToGeometryAttributeRefDataSet(attributeSet))
    const mergedAttrRefDataSet = applyGeometryOperator((...attrRefSets: AttributeRefSet[]) => Operators.merge(...attrRefSets), ...attrRefDataSets)
    return geometryAttributeRefDataSetToGeometryAttributeSetData(mergedAttrRefDataSet, attributeSets[0].dim)
}

export abstract class GeometryMergeOperatorNode<
    out T extends IndexingDim,
    out TAttributeSet extends EvaluableTemplateNode<GeometryAttributeSetData<T> | null>,
> extends GeometryAttributeSetNode<T, MergeOperatorParameters<TAttributeSet>> {
    protected override hasValidParameters(
        parameters: MergeOperatorParameters<TAttributeSet>,
    ): parameters is MergeOperatorParameters<TAttributeSet> & NonNullableParameters<MergeOperatorParameters<TAttributeSet>> {
        return parameters.attributeSets.length > 0
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<MergeOperatorParameters<TAttributeSet>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeSetData<T> | null>; invalidator?: BuilderInlet<null>} {
        const {attributeSets} = parameters

        const [attributeSetsDataValid, attributeSetsDataInvalid] = scope.branch(
            scope.pureLambda(
                scope.list(attributeSets.map((attrSet) => evaluator.evaluate(attrSet))),
                (attributeSetsData) => (attributeSetsData.every((data) => data !== null) ? attributeSetsData : null),
                "evaluateAttributeSets",
            ),
        )

        const geometryAttributeSetData = scope.pureLambda(
            attributeSetsDataValid,
            (attributeSetsData) => applyMergeOperator(attributeSetsData),
            "applyMergeOperator",
        )

        return {evaluatedGeometryData: geometryAttributeSetData, invalidator: attributeSetsDataInvalid}
    }
}
